/**
 * Tabella CVE del report SRP con le informazioni di contesto del triage vulnerabilità.
 *
 * La lista CVE del report (`SrpCve`) porta solo cveId/severità/componente; le colonne
 * di contesto (Stato VEX, Giustificazione, Descrizione) vivono nei finding del triage,
 * esposti da GET /releases/:bundleProjectUuid/triage (aggregation-service). Qui riusiamo
 * `fetchTriage` — una query per ciascun `bundleProjectUuid` distinto presente fra le CVE
 * (stessa queryKey del triage, quindi cache condivisa) — e uniamo per `cveId` per
 * arricchire ogni riga. Lo stato VEX resta in sola lettura (si modifica nel triage); con
 * `onRimuovi` ogni riga offre la rimozione della CVE dal report.
 */

import { useQueries } from '@tanstack/react-query';
import { fetchTriage } from '../triage/api.js';
import { triageKeys } from '../triage/hooks.js';
import type { TriageItem } from '../triage/types.js';
import type { SrpCve } from './types.js';
import srpStyles from './Srp.module.css';
import triageStyles from '../triage/Triage.module.css';

// ---------------------------------------------------------------------------
// Badge helper (stessa logica di features/triage/TriagePage.tsx)
// ---------------------------------------------------------------------------

function troncaTesto(testo: string, max: number): string {
  return testo.length > max ? `${testo.slice(0, max).trimEnd()}…` : testo;
}

function classeSeverita(severity: string | undefined): string {
  const sev = (severity ?? '').toUpperCase();
  if (sev === 'CRITICAL') return triageStyles.badgeCritical ?? '';
  if (sev === 'HIGH') return triageStyles.badgeHigh ?? '';
  if (sev === 'MEDIUM') return triageStyles.badgeMedium ?? '';
  if (sev === 'LOW') return triageStyles.badgeLow ?? '';
  return triageStyles.badgeInfo ?? '';
}

function classeStato(stato: string): string {
  const b = triageStyles.badge ?? '';
  if (stato === 'RESOLVED') return `${b} ${triageStyles.badgeStateResolved ?? ''}`;
  if (stato === 'FALSE_POSITIVE') return `${b} ${triageStyles.badgeStateFalsePositive ?? ''}`;
  if (stato === 'NOT_AFFECTED') return `${b} ${triageStyles.badgeStateNotAffected ?? ''}`;
  if (stato === 'EXPLOITABLE') return `${b} ${triageStyles.badgeStateExploitable ?? ''}`;
  return `${b} ${triageStyles.badgeState ?? ''}`;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export interface SrpCveTableProps {
  cves: SrpCve[];
  /** Se presente, colonna «Azioni» con il bottone «Rimuovi» per ogni CVE. */
  onRimuovi?: (cveId: string) => void;
  /** Disabilita i bottoni di rimozione (mutazione in corso). */
  rimozioneDisabilitata?: boolean;
}

export function SrpCveTable({ cves, onRimuovi, rimozioneDisabilitata }: SrpCveTableProps) {
  // Bundle DT distinti presenti fra le CVE: una query triage per ciascuno.
  const bundleUuids = Array.from(
    new Set(cves.map((c) => c.bundleProjectUuid).filter((u): u is string => !!u)),
  );

  const results = useQueries({
    queries: bundleUuids.map((uuid) => ({
      queryKey: triageKeys.lista(uuid),
      queryFn: () => fetchTriage(uuid),
      staleTime: 60_000,
    })),
  });

  const itemsByBundle = new Map<string, TriageItem[]>();
  bundleUuids.forEach((uuid, i) => {
    itemsByBundle.set(uuid, results[i]?.data?.items ?? []);
  });
  const isFetching = results.some((r) => r.isLoading);

  /** Finding di triage corrispondente alla CVE (match per cveId, poi per componente). */
  function triagePerCve(cve: SrpCve): TriageItem | undefined {
    if (!cve.bundleProjectUuid) return undefined;
    const stessaCve = (itemsByBundle.get(cve.bundleProjectUuid) ?? []).filter(
      (it) => it.cveId === cve.cveId,
    );
    if (stessaCve.length === 0) return undefined;
    if (cve.componentName) {
      const esatto = stessaCve.find(
        (it) =>
          it.componentName === cve.componentName &&
          (!cve.componentVersion || it.componentVersion === cve.componentVersion),
      );
      if (esatto) return esatto;
    }
    return stessaCve[0];
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={srpStyles.table} data-testid="srp-cve-table">
        <thead>
          <tr>
            <th>CVE ID</th>
            <th>Severità</th>
            <th>Componente</th>
            <th>Stato VEX</th>
            <th>Giustificazione</th>
            <th>Descrizione</th>
            {onRimuovi && <th aria-label="Azioni" />}
          </tr>
        </thead>
        <tbody>
          {cves.map((cve) => {
            const triage = triagePerCve(cve);
            const severita = cve.severity ?? triage?.severity;
            const componente = cve.componentName ?? triage?.componentName;
            const versione = cve.componentVersion ?? triage?.componentVersion;
            const statoVex = triage?.analysisState;
            const giustificazione = triage?.justification;
            const descrizione = triage?.description;

            return (
              <tr key={cve.cveId} data-testid={`cve-${cve.cveId}`}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{cve.cveId}</span>
                </td>
                <td>
                  {severita ? (
                    <span className={`${triageStyles.badge} ${classeSeverita(severita)}`}>
                      {severita}
                    </span>
                  ) : (
                    <span className={`${triageStyles.badge} ${triageStyles.badgeInfo}`}>N/D</span>
                  )}
                </td>
                <td>
                  {componente ? (
                    <>
                      <span style={{ fontWeight: 500 }}>{componente}</span>
                      {versione && (
                        <span
                          style={{ color: '#6c757d', marginLeft: '0.4rem', fontSize: '0.82rem' }}
                        >
                          {versione}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#adb5bd' }}>—</span>
                  )}
                </td>
                <td>
                  {statoVex ? (
                    <span className={classeStato(statoVex)}>{statoVex}</span>
                  ) : isFetching ? (
                    <span style={{ color: '#adb5bd' }}>…</span>
                  ) : (
                    <span style={{ color: '#adb5bd' }}>—</span>
                  )}
                </td>
                <td>
                  {giustificazione ? (
                    <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>{giustificazione}</span>
                  ) : (
                    <span style={{ color: '#adb5bd' }}>—</span>
                  )}
                </td>
                <td data-testid={`descrizione-cve-${cve.cveId}`}>
                  {descrizione ? (
                    <span className={triageStyles.descCell} tabIndex={0}>
                      {troncaTesto(descrizione, 160)}
                      <span className={triageStyles.descTooltip} role="tooltip">
                        {descrizione}
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: '#adb5bd' }}>—</span>
                  )}
                </td>
                {onRimuovi && (
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button
                      type="button"
                      className={`${srpStyles.btnSecondary} ${srpStyles.btnSmall}`}
                      data-testid={`rimuovi-cve-${cve.cveId}`}
                      title={`Rimuovi ${cve.cveId} dal report`}
                      disabled={rimozioneDisabilitata}
                      onClick={() => {
                        onRimuovi(cve.cveId);
                      }}
                    >
                      Rimuovi
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
